import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth, onAuthStateChanged } from '../auth';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Alert from '../components/ui/Alert';
import PlusLeagueUpsellSheet from '../components/groups/PlusLeagueUpsellSheet';
import { joinGroupViaLink, previewGroupInviteLink } from '../api/groupInviteLinkService';
import { fetchUserProfile, hasRealProfile as profileIsComplete } from '../api/userProfileService';
import { CHECKOUT_SOURCES } from '../constants/checkoutAttribution';
import { usePlusCheckout } from '../hooks/usePlusCheckout';
import { setPendingJoinToken } from '../utils/joinLeagueIntent';
import { isJoinCapError } from '../utils/checkoutIntent';
import { typography } from '../design-system/tokens';

function parseJoinToken(pathname) {
  const match = String(pathname || '').match(/^\/join\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export default function JoinLeaguePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = useMemo(() => parseJoinToken(location.pathname), [location.pathname]);

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(undefined);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [upsellOpen, setUpsellOpen] = useState(false);

  const { startPlusCheckout, checkoutLoading } = usePlusCheckout({
    onError: (msg) => setError(msg),
  });

  useEffect(() => {
    if (token) setPendingJoinToken(token);
  }, [token]);

  useEffect(() => {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser?.uid) {
        try {
          setProfile(await fetchUserProfile(firebaseUser.uid));
        } catch (_) {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    });
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Invalid invite link.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    previewGroupInviteLink(token)
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setPreview(null);
          setError(err?.message || 'Could not load invite');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const handleJoin = useCallback(async () => {
    if (!token || !user?.uid) return;
    if (profile !== undefined && !profileIsComplete(profile)) {
      navigate('/create-profile', { replace: true });
      return;
    }
    setJoining(true);
    setError('');
    try {
      const result = await joinGroupViaLink(token);
      const groupId = result?.groupId;
      if (groupId) {
        navigate(`/groups/${groupId}`, { replace: true });
      } else {
        navigate('/groups', { replace: true });
      }
    } catch (err) {
      const message = err?.message || 'Could not join league';
      if (isJoinCapError(message)) {
        setUpsellOpen(true);
      } else {
        setError(message);
      }
    } finally {
      setJoining(false);
    }
  }, [token, user?.uid, profile, navigate]);

  const handleSignIn = () => {
    if (token) setPendingJoinToken(token);
    navigate('/signup');
  };

  const profileReady = profile !== undefined && profileIsComplete(profile);
  const canJoin = Boolean(preview?.joinable) && Boolean(user?.uid) && profileReady;
  const autoJoinRef = useRef(false);

  useEffect(() => {
    if (!canJoin || autoJoinRef.current || joining) return;
    autoJoinRef.current = true;
    handleJoin();
  }, [canJoin, handleJoin, joining]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4">
        <Alert tone="error" className="text-sm font-bold">This invite link is invalid.</Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white px-4 py-10 sm:px-6">
      <div className="max-w-md mx-auto space-y-6">
        <header className="text-center space-y-2">
          <p className={`${typography.microWide} text-violet-400`}>League invite</p>
          <h1 className="text-2xl font-black">
            {loading ? 'Loading…' : preview?.groupName || 'Join a league'}
          </h1>
          {preview?.inviterDisplayName && (
            <p className="text-sm text-gray-400 font-medium">
              Invited by {preview.inviterDisplayName}
            </p>
          )}
        </header>

        {error && <Alert tone="error" className="text-sm font-bold">{error}</Alert>}

        {!loading && preview && (
          <Card variant="admin" className="p-5 space-y-3">
            {preview.description && (
              <p className="text-sm text-gray-300 leading-relaxed">{preview.description}</p>
            )}
            <p className={`${typography.microWide} text-gray-500`}>
              {preview.memberCount} member{preview.memberCount === 1 ? '' : 's'}
              {preview.sport ? ` · ${preview.sport}` : ''}
            </p>
            {!preview.joinable && preview.reason && (
              <Alert tone="warning" className="text-xs font-bold">{preview.reason}</Alert>
            )}
            {preview.joinable && preview.usesRemaining != null && (
              <p className="text-[10px] uppercase tracking-widest text-gray-600">
                Link expires in 7 days · {preview.usesRemaining} joins remaining
              </p>
            )}
          </Card>
        )}

        {preview?.joinable && !user?.uid && (
          <div className="space-y-3">
            <p className="text-sm text-gray-400 text-center font-medium">
              Sign in or create an account to join this league — no arena name needed.
            </p>
            <Button type="button" variant="admin" fullWidth onClick={handleSignIn}>
              Sign in or create account
            </Button>
            <p className="text-center">
              <Link to="/" className="text-xs font-bold text-gray-500 hover:text-gray-300">
                Back to Lockbox
              </Link>
            </p>
          </div>
        )}

        {preview?.joinable && user?.uid && !profileReady && (
          <div className="space-y-3">
            <p className="text-sm text-gray-400 text-center">Finish your profile to join.</p>
            <Button type="button" variant="admin" fullWidth onClick={() => navigate('/create-profile')}>
              Complete profile
            </Button>
          </div>
        )}

        {canJoin && (
          <Button
            type="button"
            variant="admin"
            fullWidth
            disabled={joining}
            onClick={handleJoin}
          >
            {joining ? 'Joining…' : 'Join league'}
          </Button>
        )}

        {user?.uid && preview?.joinable && (
          <p className="text-center">
            <Link to="/groups" className="text-xs font-bold text-violet-400 hover:text-violet-300">
              View my groups
            </Link>
          </p>
        )}
      </div>

      <PlusLeagueUpsellSheet
        open={upsellOpen}
        onClose={() => setUpsellOpen(false)}
        variant="join_cap"
        checkoutLoading={checkoutLoading}
        onStartTrial={() => startPlusCheckout(CHECKOUT_SOURCES.GROUP_JOIN_CAP)}
      />
    </div>
  );
}
